import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-log-list>` says about itself.
 *
 * It is a CHOOSING surface, not a reading one. Nobody reads a log list; they scan it for the one log
 * belonging to the thing that just went wrong. Hence newest first, the person and the operation in
 * full, and the size stated — a 4 MB log and a 2 KB log are different decisions about opening it.
 *
 * Time is shown BOTH ways: "2 minutes ago" for the ordinary case, and the absolute time as the
 * title for the moment somebody compares it against a report, a ticket or a colleague's screen.
 * Only the relative form makes the list useless for the second task; only the absolute form makes it
 * slow for the first.
 */
const docs: ComponentDocs = {
  aliases: ['debug logs', 'log browser', 'log history', 'recorded logs', 'apex logs'],

  answers: [
    'find the log from the thing that just went wrong',
    'see which logs belong to which person',
    'decide whether a log is worth opening before opening it',
    'clear out a full log list',
  ],

  placements: ['appPage'],

  whenToUse: 'On a logs surface, where somebody is choosing which log to read.',

  insteadUse: [
    { name: 'LogViewer', when: 'a log has been chosen and the question is what is IN it.' },
    {
      name: 'DataTable',
      when:
        'the rows are not logs. This one knows what a log is: it orders newest-first by default and ' +
        'draws time both ways.',
    },
  ],

  inputs: [
    {
      name: 'logs',
      type: 'json',
      description:
        'The recorded logs: `{ id, person?, application?, operation?, at?, status?, bytes? }[]`. A ' +
        'selection naming a log that has gone is dropped with it.',
      default: [],
    },
    {
      name: 'search',
      attribute: 'search',
      type: 'text',
      description: 'Narrows by id, person, application, operation or status.',
    },
    {
      name: 'selectedId',
      attribute: 'selected-id',
      type: 'text',
      description: 'Which log was last opened. Reflected when a row is opened, so it can be linked to.',
    },
    {
      name: 'deletable',
      attribute: 'deletable',
      type: 'boolean',
      description:
        'Show the delete control. Absent ⇒ hidden rather than disabled: a control for something this ' +
        'person cannot do is a question they should not have to ask.',
      default: false,
    },
    {
      name: 'sortKey',
      attribute: 'sort-key',
      type: 'text',
      options: ['at', 'person', 'application', 'operation', 'status', 'bytes'],
      description: 'Which column orders the rows.',
      default: 'at',
    },
    {
      name: 'sortDirection',
      attribute: 'sort-direction',
      type: 'text',
      options: ['asc', 'desc'],
      description:
        'Which way. The unset direction depends on the column, so it is a fallback rather than a ' +
        'default value: there is no single literal this could ship as.',
      fallback:
        'newest first (`desc`) for the time column — the opposite of every other column’s natural ' +
        'direction, because the log somebody wants is almost always the last one — and `asc` for ' +
        'every other column',
    },
    {
      name: 'emptyText',
      attribute: 'empty-text',
      type: 'text',
      description: 'What to say when there are no logs.',
      default: 'No logs recorded.',
    },
  ],

  events: [
    { name: 'caos-log-open', when: 'A row’s Open control is pressed.', carries: '`{ log }` — the whole entry.' },
    {
      name: 'caos-log-delete',
      when: 'Delete is pressed with rows selected.',
      carries:
        '`{ ids }`. It ASKS: clearing a log list is routine and still irreversible, and a component ' +
        'that removed rows on a click would decide on somebody’s behalf that no confirmation was needed.',
    },
    { name: 'caos-log-refresh', when: 'Refresh is pressed.', carries: 'nothing' },
  ],

  state: [
    { name: 'selectedIds', type: 'string[]', description: 'The logs ticked for an action.' },
    { name: 'visibleLogs', type: 'CaosLogEntry[]', description: 'The logs surviving the search, in the current order.' },
  ],

  parts: [
    { part: 'logs', is: 'The outer column.' },
    { part: 'toolbar', is: 'The search and action row.' },
    { part: 'search', is: 'The search box.' },
    { part: 'actions', is: 'The refresh and delete cluster.' },
    { part: 'summary', is: 'The count and selection line.' },
    { part: 'table', is: 'The composed data table.' },
    { part: 'when', is: 'The relative-time cell.' },
    { part: 'open', is: 'A row’s open control.' },
  ],

  accessibility:
    'Every row’s Open control is a real button whose accessible name says which log it opens ("Open ' +
    'the log from 2 minutes ago"), which matters on a screen holding forty identically labelled ' +
    'controls. The summary is a `role="status"` live region, so a refresh announces what arrived. ' +
    'The absolute timestamp is the relative cell’s `title`, so it is reachable rather than lost. A ' +
    'log whose size is unknown says "unknown" rather than showing 0 B.',

  examples: [
    {
      key: 'recent',
      label: 'Choosing a log',
      summary:
        'Newest first, with the person and the operation in full and the size stated — everything ' +
        'somebody needs to pick the right one without opening three.',
      placement: {
        component: 'caos-log-list',
        inputs: {
          deletable: true,
          logs: [
            {
              id: '07L1',
              person: 'jrivera',
              application: 'Browser',
              operation: 'VF Remoting',
              at: '2026-09-18T15:58:00Z',
              status: 'Success',
              bytes: 4_180_233,
            },
            {
              id: '07L2',
              person: 'integration.user',
              application: 'API',
              operation: 'Bulk Insert',
              at: '2026-09-18T15:31:00Z',
              status: 'Failed',
              bytes: 21_440,
            },
            {
              id: '07L3',
              person: 'jrivera',
              application: 'Browser',
              operation: 'Anonymous Apex',
              at: '2026-09-18T12:02:00Z',
              status: 'Success',
              bytes: 1_902,
            },
          ],
        },
      },
      stage: { needsWidth: 900 },
    },
    {
      key: 'empty',
      label: 'Nothing recorded',
      summary: 'Said, rather than left as a blank table somebody reads as a screen that failed to load.',
      placement: { component: 'caos-log-list', inputs: { logs: [] } },
      stage: { needsWidth: 900 },
    },
  ],
};

export default docs;
